/**
 * Client-side account lockout tracker.
 *
 * When the login API returns HTTP 423 (account locked), the lock timestamp is
 * persisted per normalized login identifier so the login form can disable the
 * credential fields and show a live countdown until the lock expires — even
 * across page reloads / app restarts.
 *
 * Completing a password reset clears the server-side lock, so the
 * reset-password flow also calls clearLoginLockout() to keep this store
 * in sync.
 */
import { normalizePhoneNumber } from './phoneUtils';

const LOCKOUT_STORAGE_KEY = 'ta_login_lockouts';

type LockoutMap = Record<string, number>;

function readLockoutMap(): LockoutMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(LOCKOUT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeLockoutMap(map: LockoutMap): void {
  if (typeof window === 'undefined') return;
  try {
    if (Object.keys(map).length === 0) {
      localStorage.removeItem(LOCKOUT_STORAGE_KEY);
    } else {
      localStorage.setItem(LOCKOUT_STORAGE_KEY, JSON.stringify(map));
    }
  } catch {
    // ignore storage failures
  }
}

/**
 * Normalize an identifier the same way the login submit path does
 * (phone normalization + lowercase/trim) so keys match the account the
 * backend locked.
 */
export function getLoginLockoutKey(identifier: string): string {
  return normalizePhoneNumber(identifier || '').toLowerCase().trim();
}

/** Returns the lock expiry (epoch ms) for an identifier, or null. */
export function getLoginLockoutUntil(identifier: string): number | null {
  const key = getLoginLockoutKey(identifier);
  if (!key) return null;
  const until = readLockoutMap()[key];
  return typeof until === 'number' && Number.isFinite(until) ? until : null;
}

export function saveLoginLockout(identifier: string, lockedUntilMs: number): void {
  const key = getLoginLockoutKey(identifier);
  if (!key || !Number.isFinite(lockedUntilMs)) return;
  const map = readLockoutMap();
  map[key] = lockedUntilMs;
  writeLockoutMap(map);
}

export function clearLoginLockout(identifier: string): void {
  const key = getLoginLockoutKey(identifier);
  if (!key) return;
  const map = readLockoutMap();
  if (key in map) {
    delete map[key];
    writeLockoutMap(map);
  }
}
